import { ViewOnlyRestrictionWhen } from '@/components/custom-roles';
import { AppSpinner } from '@/ui/app-spinner';
import { FlexContainer } from '@/ui/styled-ui';
import { CheckMarkIcon } from '@/utils/helpers';
import { Skeleton } from 'primereact/skeleton';
import React, { useState } from 'react';
import styled from 'styled-components';
import { AppButton } from '../buttons';

type BlankPageProps = {
  image: string;
  title: string;
  preDescription?: string;
  description: string;
  secondaryDescription?: string;
  isLoading?: boolean;
  mainCtaText?: string;
  mainCtaOnClick?: () => void;
  mainCtaRestricted?: boolean;
  mainBookingLink?: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  secondaryCtaOnClick?: () => void;
  secondaryBtnType?: 'outlined' | 'text';
  secondaryBtnSeverity?: 'underline' | 'no-background';
  additionsText?: string[];
};

const StyledBlankContainer = styled(FlexContainer)`
  flex-direction: column;
  background-color: var(--beige-main);
  border-radius: var(--medium-border-radius);
  flex: 1;
  overflow: hidden;
  padding: var(--default-padding);

  h2,
  p {
    max-width: 628px;
    text-align: center;
    margin: 0;
  }

  h2 {
    font-size: var(--heading-medium-font-size);
    line-height: var(--heading-medium-line-height);
    margin-block: var(--medium-padding);
  }
  p {
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);
    margin-bottom: var(--semi-big-padding);
  }
`;

const StyledAdditionsContainer = styled(FlexContainer)`
  max-width: 628px;
  background-color: var(--white-main);
  border: 1px solid var(--gray-medium);
  border-radius: var(--xsmall-border-radius);
  padding: var(--medium-padding) var(--default-padding);
  margin-top: var(--big-padding);
  gap: var(--default-padding);
  align-items: stretch;
`;

const StyledAddition = styled(FlexContainer)`
  height: 100%;
  text-align: center;
  flex-direction: column;
  justify-content: flex-start;

  span {
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
  }

  &:not(:first-child) {
    padding-left: var(--default-padding);
    border-left: 1px solid var(--gray-medium);
  }
`;

const IMAGE_HEIGHT = 320;
const IMAGE_MAX_WIDTH = 682;

const StyledBlankPageImageWrapper = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: ${IMAGE_MAX_WIDTH}px;
  max-height: ${IMAGE_HEIGHT}px;
  margin: 0 auto;
`;

const StyledBlankPageImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: auto;
  margin: 0 auto;
`;

const Addition = ({ text }: { text: string }) => {
  return (
    <StyledAddition direction="column">
      <CheckMarkIcon />
      <span className="mt-1" dangerouslySetInnerHTML={{ __html: text }} />
    </StyledAddition>
  );
};

export const BlankPage: React.FC<BlankPageProps> = ({
  image,
  title,
  preDescription,
  description,
  secondaryDescription,
  isLoading,
  mainCtaText,
  mainCtaOnClick,
  mainCtaRestricted = false,
  mainBookingLink,
  secondaryCtaText,
  secondaryCtaLink,
  secondaryCtaOnClick,
  secondaryBtnType,
  secondaryBtnSeverity = 'underline',
  additionsText,
}) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  return (
    <StyledBlankContainer>
      {isLoading ? (
        <FlexContainer direction="column" height="100%" className="mt-5">
          <AppSpinner />
        </FlexContainer>
      ) : (
        <>
          {!isImageLoaded ? (
            <Skeleton
              height={`${IMAGE_HEIGHT}px`}
              width={`${IMAGE_MAX_WIDTH}px`}
            />
          ) : null}
          <StyledBlankPageImageWrapper
            style={{ display: isImageLoaded ? 'block' : 'none' }}
          >
            <StyledBlankPageImage
              src={image}
              alt="empty-page-image"
              onLoad={() => setIsImageLoaded(true)}
            />
          </StyledBlankPageImageWrapper>
          <h2>{title}</h2>
          {preDescription && <p>{preDescription}</p>}
          <p>{description}</p>
          {secondaryDescription && <p>{secondaryDescription}</p>}
          {((mainCtaText && (mainCtaOnClick || mainBookingLink)) ||
            (secondaryCtaText &&
              (secondaryCtaLink || secondaryCtaOnClick))) && (
            <FlexContainer gap={16} className="mb-2">
              {mainBookingLink && mainCtaText && (
                <a
                  href={mainBookingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AppButton label={mainCtaText} severity="success" />
                </a>
              )}
              {mainCtaText && mainCtaOnClick && (
                <ViewOnlyRestrictionWhen restricted={mainCtaRestricted}>
                  <AppButton
                    label={mainCtaText}
                    severity="success"
                    isDisabled={mainCtaRestricted}
                    onClick={mainCtaRestricted ? undefined : mainCtaOnClick}
                  />
                </ViewOnlyRestrictionWhen>
              )}
              {secondaryCtaText &&
                (secondaryCtaOnClick ? (
                  <AppButton
                    label={secondaryCtaText}
                    type={secondaryBtnType}
                    severity={secondaryBtnSeverity}
                    onClick={secondaryCtaOnClick}
                  />
                ) : secondaryCtaLink ? (
                  <a
                    href={secondaryCtaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppButton
                      label={secondaryCtaText}
                      severity={secondaryBtnSeverity}
                      type={secondaryBtnType}
                    />
                  </a>
                ) : null)}
            </FlexContainer>
          )}
          {additionsText && (
            <StyledAdditionsContainer>
              {additionsText.map((text: string, index: number) => (
                <Addition key={index} text={text} />
              ))}
            </StyledAdditionsContainer>
          )}
        </>
      )}
    </StyledBlankContainer>
  );
};
